const DEFAULT_MAX_SCORE = 10;

const resolveMaxScore = (maxScore?: number | null): number =>
  typeof maxScore === 'number' && maxScore > 0 ? maxScore : DEFAULT_MAX_SCORE;

/**
 * Helper function to extract numeric ID from string ID
 */
export const extractNumericSubstateId = (substateId: string | undefined): number => {
  if (!substateId) return 0;
  const match = substateId.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

/**
 * Helper function to map skill substate ID to chip type
 */
export const getChipTypeFromSubstateId = (
  substateId: number,
): 'in-progress' | 'assessment-result' | 'revisit' | 'inferred' => {
  switch (substateId) {
    case 3: // Inferred via diagnostic
    case 4: // Inferred via assessment
      return 'inferred';
    case 5: // Passed via assessment
      return 'assessment-result';
    case 6: // Passed via teacher
      return 'assessment-result';
    case 8: // Discrepancy between diagnostic/assessment
    case 9: // Discrepancy between assessment/assessment
    case 10: // Discrepancy between diagnostic/diagnostic
      return 'revisit';
    case 11: // Passed via exit slip
      return 'assessment-result';
    case 12: // Inferred via exit slip
      return 'inferred';
    case 13: // Discrepancy between exit slip/assessment
      return 'revisit';
    case 14: // Discrepancy between exit slip/diagnostic
      return 'revisit';
    default:
      return 'assessment-result';
  }
};

/**
 * Helper function to get inferred chip text based on substate
 */
export const getInferredChipText = (substateId: number): string => {
  switch (substateId) {
    case 3: // Inferred via diagnostic
      return 'Proven on diagnostic';
    case 4: // Inferred via assessment
      return 'Proven with another skill';
    case 12: // Inferred via exit slip
      return 'Proven with exit slip';
    default:
      return '';
  }
};

/**
 * Helper function to convert score to star rating (0-5)
 */
export const parseLearnosityScoresIntoStars = (score: number, maxScore?: number | null) => {
  return Math.round((score / resolveMaxScore(maxScore)) * 5);
};

/**
 * Helper function to get star-based message with stars count
 */
export const getStarMessageWithCount = (score: number, maxScore?: number | null) => {
  if (!score && score !== 0) return null;

  const stars = Math.round((score / resolveMaxScore(maxScore)) * 5);

  switch (stars) {
    case 0:
    case 1:
      return { stars, message: 'Not there yet' };
    case 2:
      return { stars, message: 'Getting there' };
    case 3:
      return { stars, message: 'Almost there' };
    case 4:
      return { stars, message: 'Got it!' };
    case 5:
      return { stars, message: 'Perfect score' };
    default:
      return null;
  }
};

/**
 * Helper function to get score color based on assessment score
 */
export const getScoreColor = (score: number, maxScore?: number | null) => {
  const percentage = (score / resolveMaxScore(maxScore)) * 100;

  if (percentage >= 80) return 'tw-text-indigo-500';
  return 'tw-text-neutral-950';
};

/**
 * Helper function to determine if score should be bold
 */
export const shouldScoreBeBold = (score: number, maxScore?: number | null) => {
  const percentage = (score / resolveMaxScore(maxScore)) * 100;
  return percentage >= 80;
};

/**
 * Required skill substate IDs for recent history filtering
 */
export const REQUIRED_SUBSTATE_IDS = [4, 5, 6, 8, 9, 10];

/**
 * Interface for skill history entry
 */
export interface SkillHistoryEntry {
  skillId: string;
  skillName: string;
  skillCode: string;
  activityDate: Date;
  chipType: 'in-progress' | 'assessment-result' | 'revisit' | 'inferred';
  assessmentScore?: number;
  maxScore?: number;
  targetSkillType?: string | null;
  isNested?: boolean;
  inferredBy?: string;
  skillSubstateId?: number;
  skillSubstateName?: string;
}

/**
 * Interface for rating display options
 */
export interface RatingDisplayOptions {
  showStars?: boolean;
  showMessage?: boolean;
  customClassName?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'simple' | 'detailed' | 'compact';
}

/**
 * Helper function to get rating display configuration
 */
export const getRatingDisplayConfig = (options: RatingDisplayOptions = {}): Required<RatingDisplayOptions> => {
  return {
    showStars: options.showStars ?? true,
    showMessage: options.showMessage ?? true,
    customClassName: options.customClassName ?? '',
    size: options.size ?? 'medium',
    variant: options.variant ?? 'detailed',
  };
};

/**
 * Helper function to get rating size classes
 */
export const getRatingSizeClasses = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return 'tw-text-xs tw-gap-1';
    case 'large':
      return 'tw-text-lg tw-gap-3';
    default:
      return 'tw-text-sm tw-gap-2';
  }
};

/**
 * Helper function to get rating variant classes
 */
export const getRatingVariantClasses = (variant: 'simple' | 'detailed' | 'compact') => {
  switch (variant) {
    case 'simple':
      return 'tw-flex tw-items-center';
    case 'compact':
      return 'tw-flex tw-items-center tw-gap-1';
    default:
      return 'tw-flex tw-items-center tw-gap-2';
  }
};
